/**
 * Dosha checks, yoga extraction and life-report grouping for the Kundli
 * Studio. The doshas apply the standard classical rules to LIVE chart facts
 * from the API (planet houses and longitudes from AllPlanetData, Saturn's
 * current transit) — VedAstro has no single-person dosha endpoints, only
 * two-chart kuta comparisons. Yogas and the report come straight from the
 * `HoroscopePredictions` engine.
 */

import { callMethod, buildTime, NEW_DELHI, SIGN_ORDER, getHoroscopePredictions } from './vedastro.js'
import { memoized } from './kundliData.js'

/* ─────────────────────────── Predictions (shared) ─────────────────────────── */

/** Memoised HoroscopePredictions per birth — Yogas and Life Report share it. */
export function getPredictionsCached({ dob, tob, location }, signal) {
  return memoized(`preds|${dob}|${tob}|${location?.Name}`, () =>
    getHoroscopePredictions({ dob, tob, location }, signal))
}

/** The sidereal sign Saturn transits on a date (1 call, memoised per date). */
export async function getSaturnTransitSign(dateStr, signal) {
  return memoized(`saturn-transit|${dateStr}`, async () => {
    const time = buildTime(dateStr, '12:00', NEW_DELHI)
    const r = await callMethod('PlanetRasiD1Sign', { planetName: { Name: 'Saturn' }, time }, signal)
    return r?.Name ?? r
  })
}

/* ─────────────────────────── Doshas ─────────────────────────── */

// Houses in which Mars gives Mangal dosha, counted from Lagna or Moon.
const MANGLIK_HOUSES = [1, 2, 4, 7, 8, 12]

/** Mangal (Manglik) dosha from both reference points, per the classical rule. */
export function computeManglik(planets) {
  const mars = planets.find((p) => p.name === 'Mars') || {}
  const moon = planets.find((p) => p.name === 'Moon') || {}
  const moonIdx = SIGN_ORDER.indexOf(moon.sign)
  const marsIdx = SIGN_ORDER.indexOf(mars.sign)
  const fromLagna = mars.house || null
  const fromMoon = moonIdx >= 0 && marsIdx >= 0 ? ((marsIdx - moonIdx + 12) % 12) + 1 : null
  const lagnaManglik = MANGLIK_HOUSES.includes(fromLagna)
  const moonManglik = fromMoon != null && MANGLIK_HOUSES.includes(fromMoon)
  return { fromLagna, fromMoon, lagnaManglik, moonManglik, present: lagnaManglik || moonManglik }
}

/**
 * Kalsarpa dosha — all seven grahas caught on one side of the Rahu–Ketu axis,
 * checked with absolute sidereal longitudes.
 */
export function computeKalsarpa(planets) {
  const rahu = planets.find((p) => p.name === 'Rahu')
  const bodies = planets.filter((p) => p.name !== 'Rahu' && p.name !== 'Ketu')
  if (!rahu || bodies.length < 7) return null
  const inRahuHalf = (deg) => ((deg - rahu.absDeg + 360) % 360) < 180
  const inside = bodies.filter((b) => inRahuHalf(b.absDeg)).length
  const present = inside === bodies.length || inside === 0
  return {
    present,
    // How many grahas break the axis when the dosha is absent.
    outsideCount: present ? 0 : Math.min(inside, bodies.length - inside),
  }
}

/**
 * Sadesati — Saturn transiting the 12th, 1st or 2nd sign from the natal Moon.
 * `saturnSign` comes from getSaturnTransitSign for today.
 */
export function sadesatiStatus(natalMoonSign, saturnSign) {
  const m = SIGN_ORDER.indexOf(natalMoonSign)
  const s = SIGN_ORDER.indexOf(saturnSign)
  if (m < 0 || s < 0) return null
  const houseFromMoon = ((s - m + 12) % 12) + 1
  const phase =
    houseFromMoon === 12 ? 'Rising — first phase' :
      houseFromMoon === 1 ? 'Peak — middle phase' :
        houseFromMoon === 2 ? 'Setting — final phase' : null
  return { active: phase != null, phase, houseFromMoon, saturnSign }
}

/* ─────────────────────────── Yogas & life report ─────────────────────────── */

/** "SunaphaYoga" / "Lord2InHouse3_X" → readable title. */
export function prettifyPredictionName(name) {
  return String(name || '')
    .split('_')[0]
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/(\d+)([A-Z])/g, '$1 $2')
    .trim()
}

/** Named planetary yogas among the predictions (tagged Yoga or *Yoga names). */
export function extractYogas(predictions) {
  return predictions.filter((p) => (p.tags || []).includes('Yoga') || /yoga/i.test(p.name))
}

// Life-report groups in priority order — the first tag match wins.
export const REPORT_GROUPS = [
  { key: 'personality', label: 'Personality & Mind', icon: '🧠', tags: ['Personality', 'Character', 'Personal', 'Appearance', 'Style', 'Emotions', 'MentalHealth'] },
  { key: 'wealth', label: 'Wealth & Finance', icon: '💰', tags: ['Wealth', 'Finance', 'Property', 'Inheritance', 'Business'] },
  { key: 'career', label: 'Career & Status', icon: '💼', tags: ['Career', 'Reputation', 'Status', 'Legal'] },
  { key: 'love', label: 'Love & Marriage', icon: '💞', tags: ['Marriage', 'Romance', 'SexualHealth'] },
  { key: 'family', label: 'Family & Children', icon: '👪', tags: ['Family', 'Children', 'Parents', 'Siblings', 'Home'] },
  { key: 'health', label: 'Health & Wellbeing', icon: '🌿', tags: ['Health', 'MedicalAstrology', 'WellBeing', 'OrganHealth', 'Disease', 'Lifespan'] },
  { key: 'learning', label: 'Learning & Arts', icon: '📚', tags: ['Education', 'Learning', 'Arts', 'Creativity'] },
  { key: 'journeys', label: 'Travel & Spirit', icon: '🧭', tags: ['Travel', 'Migration', 'Lifestyle', 'Spirituality'] },
  { key: 'caution', label: 'Cautions', icon: '⚠️', tags: ['Risk', 'Danger', 'Adversity', 'Conflict', 'Crime', 'Imprisonment', 'Famine'] },
]

// Maraka/death predictions stay out of the public self-serve report — that
// reading belongs in a guided consultation, not a free web tool.
const EXCLUDED_TAGS = ['Death']

/** Bucket predictions into REPORT_GROUPS (+ Other), dropping excluded tags. */
export function groupPredictions(predictions) {
  const groups = REPORT_GROUPS.map((g) => ({ ...g, items: [] }))
  const other = { key: 'other', label: 'Other Insights', icon: '✨', items: [] }
  for (const p of predictions) {
    const tags = p.tags || []
    if (tags.some((t) => EXCLUDED_TAGS.includes(t))) continue
    const group = groups.find((g) => g.tags.some((t) => tags.includes(t)))
    ;(group || other).items.push(p)
  }
  return [...groups, other].filter((g) => g.items.length)
}
